import { Button } from '@/components/ui/elements/button';
import { ButtonGroup } from '@/components/ui/elements/button-group';
import { PolicyGate } from '@/features/auth/authorization';
import useNotificationStore from '@/stores/notifications';
import { cn } from '@/utils/format';
import {
  BellIcon,
  BellIcon as BellIconSolid,
  BookmarkIcon,
  CalendarIcon,
  MapPinIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/solid';
import { useShallow } from 'zustand/react/shallow';
import { useOverlayContext } from '../../OverlayControls';

export const DesktopOverlayControls = () => {
  const { trayDirection, trayVisible, trayMode, togglePanels, setTrayMode, toggleDirection } =
    useOverlayContext();

  const { notifications } = useNotificationStore(
    useShallow((state) => ({
      notifications: state.notifications,
    })),
  );

  const unread = notifications.filter((notification) => !notification.read).length || 0;

  const handleCalendarIconClick = () => {
    if (trayMode === 'calendar' && trayVisible) {
      togglePanels();
    } else {
      if (!trayVisible) togglePanels();
      setTrayMode('calendar');
    }
  };

  const handleMapIconClick = () => {
    if (trayMode === 'map' && trayVisible) {
      togglePanels();
    } else {
      if (!trayVisible) togglePanels();
      setTrayMode('map');
    }
  };

  const handleNotificationIconClick = () => {
    if (trayMode === 'notifications' && trayVisible) {
      togglePanels();
    } else {
      if (!trayVisible) togglePanels();
      setTrayMode('notifications');
    }
  };

  const handlePinsIconClick = () => {
    if (trayMode === 'pins' && trayVisible) {
      togglePanels();
    } else {
      if (!trayVisible) togglePanels();
      setTrayMode('pins');
    }
  };

  return (
    <ButtonGroup className="my-3 mx-auto">
      <PolicyGate policy="calendar.view">
        <Button
          onClick={handleCalendarIconClick}
          variant="outline"
          size="icon"
          className={cn(trayVisible && trayMode === 'calendar' ? 'bg-primary text-white' : '')}
        >
          <span className="sr-only">Show Calendar</span>
          <CalendarIcon aria-hidden="true" />
        </Button>
      </PolicyGate>
      <Button
        onClick={handleMapIconClick}
        variant="outline"
        size="icon"
        className={cn(trayVisible && trayMode === 'map' ? 'bg-primary text-white' : '')}
      >
        <span className="sr-only">Show Map</span>
        <MapPinIcon aria-hidden="true" />
      </Button>
      <Button
        onClick={handleNotificationIconClick}
        variant="outline"
        size="icon"
        className={cn(
          trayVisible && trayMode === 'notifications' ? 'bg-primary text-white' : '',
          !!unread && trayMode === 'notifications' && trayVisible
            ? 'bg-red-500 hover:bg-red-600'
            : '',
          !!unread && (trayMode !== 'notifications' || !trayVisible)
            ? 'text-red-500 hover:bg-red-600'
            : '',
        )}
      >
        <span className="sr-only">Show Notifications</span>
        {unread > 0 ? (
          <BellIconSolid className={cn('animate-bounce')} aria-hidden="true" />
        ) : (
          <BellIcon aria-hidden="true" />
        )}
      </Button>
      <Button
        onClick={handlePinsIconClick}
        variant="outline"
        size="icon"
        className={cn(trayVisible && trayMode === 'pins' ? 'bg-primary text-white' : '')}
      >
        <span className="sr-only">Show Pins</span>
        <BookmarkIcon aria-hidden="true" />
      </Button>
      <Button onClick={toggleDirection} variant="outline" size="icon" disabled={!trayVisible}>
        <span className="sr-only">Switch View Mode</span>
        <ViewColumnsIcon
          className={cn(trayDirection === 'horizontal' ? '' : 'rotate-90')}
          aria-hidden="true"
        />
      </Button>
    </ButtonGroup>
  );
};
